/**
 * Copyright 2014-present CloudIO Inc. All Rights Reserved.
 * @format
 *
 */

import {
  DBRow,
  IoDictionary,
  IoDictionaryTl,
  IoLanguages,
} from '@cloudio-saas/datasource-types';
import { useLatest, useStateXAtomValue } from '@cloudio-saas/statex';
import { useStoreRecords } from '../components/DataStoreReference/DataStoreReference';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useUser from '../root/useUser';
import useDataStore from '../store/useDataStore';
import { languageFilter, useIoLanguagesStore } from '../store/utils/utils';
import { recordDictionaryAtom, userLangAtom } from './state';

interface Dictionary {
  [key: string]: string;
}

export type TranslateFn = (key: string | null | undefined) => string;

let globalT: TranslateFn = (key: string | null | undefined) => key || '';

export function translate(fullText: string) {
  return globalT(fullText);
}

const TranslateContext = createContext<{ t: TranslateFn }>({
  t: (key: string | null | undefined) => {
    return key ?? '';
  },
});

/**
 * Use this hook to translate a string
 * @returns {TranslateFn} {t: function to translate a string}
 */
export function useTranslate(): { t: TranslateFn } {
  return useContext(TranslateContext);
}

const dictionaryCache: Record<string, Dictionary> = {};

export function useLanguages(appUid: string) {
  const store = useIoLanguagesStore(appUid);
  store.read({ props: languageFilter(appUid) });
  const rows = useStoreRecords(store) as DBRow<IoLanguages>[];
  return rows;
}

export function useRecording(appUid: string) {
  const rows = useLanguages(appUid);
  const { userName } = useUser();
  const allRecordedWordsRef = useRef<string[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const enableRecording = useStateXAtomValue(recordDictionaryAtom, {
    params: { userName },
  });

  const store = useDataStore<IoDictionary>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDictionary',
    alias: 'IoDictionaryAlias',
  });
  if (enableRecording) {
    store.read({
      props: {
        filter: [{ appUid: { is: appUid } }],
        selectAttributeCodeList: ['dictCode'],
        limit: 20000,
      },
    });
  }

  const allWords = useMemo(
    () =>
      // just to make sure appUid is part of the dependency array
      appUid
        ? (Object.values(store.records()) as DBRow<IoDictionary>[]).map(
            (row) => row.dictCode,
          )
        : [],
    [store, appUid],
  );
  allRecordedWordsRef.current = allWords;

  const scheduleTimer = useCallback(() => {
    if (timeoutRef.current) return;
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      store.save({ silent: true, feedback: 'NONE' });
    }, 5000);
  }, [store]);

  useEffect(() => {
    if (!enableRecording) {
      if (timeoutRef.current) {
        store.save({ silent: true, feedback: 'NONE' });
        clearTimeout(timeoutRef.current);
      }
    }
  }, [enableRecording, store]);

  const record = useCallback(
    (fullText: string) => {
      if (!enableRecording || rows.length === 0 || !fullText) {
        return;
      }
      fullText = fullText.trim();
      const dictCode =
        fullText.length > 128 ? fullText.substring(0, 128) : fullText;
      if (!allRecordedWordsRef.current.includes(dictCode)) {
        allRecordedWordsRef.current.push(dictCode);
        store.createNew({
          partialRecord: { dictCode, fullText, appUid },
          rs: 'I',
        });
        scheduleTimer();
      }
    },
    [appUid, enableRecording, rows, scheduleTimer, store],
  );
  return record;
}

const EMPTY_DICTIONARY: Dictionary = {};

export function TranslationProvider({
  children,
  appUid,
}: {
  children: React.ReactNode;
  appUid: string;
}) {
  const rows = useLanguages(appUid);
  const { userName } = useUser();
  const [dict, setDict] = useState<Dictionary>(EMPTY_DICTIONARY);

  const userLanguage = useStateXAtomValue(userLangAtom, {
    params: { userName },
  });
  const enableRecording = useStateXAtomValue(recordDictionaryAtom, {
    params: { userName },
  });

  let lang = rows.find((row) => row.languageCode === userLanguage);

  if (!lang) {
    lang = rows.find((row) => row.primaryLanguage === 'Y');
  }
  const cacheKey = `${appUid}_${lang?.languageCode}`;
  const store = useDataStore<IoDictionaryTl>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDictionaryTl',
    alias: 'IoDictionaryTlAlias',
  });
  const drows = useStoreRecords(store) as DBRow<IoDictionaryTl>[];

  useEffect(() => {
    // do not re-query if the dictionary is already cached
    if (lang && !dictionaryCache[cacheKey]) {
      const { languageCode } = lang;
      // query the dictionary from the server
      store.query({
        props: {
          filter: [
            { langCode: { is: languageCode } },
            { appUid: { is: appUid } },
          ],
          selectAttributeCodeList: ['dictCode', 'dictMeaning'],
          limit: 20000,
        },
      });
    }
  }, [appUid, cacheKey, lang, store]);

  useEffect(() => {
    const _dict = dictionaryCache[cacheKey];
    if (_dict) {
      // dictionary is already loaded
      setDict(_dict);
    } else {
      setDict(EMPTY_DICTIONARY);
    }
  }, [cacheKey]);

  const cacheKeyRef = useLatest(cacheKey);
  useEffect(() => {
    // new dictionary queried from the server
    let _dict = EMPTY_DICTIONARY;
    if (drows.length > 0) {
      _dict = drows.reduce((acc, row) => {
        const { dictCode, dictMeaning } = row;
        acc[dictCode] = dictMeaning;
        return acc;
      }, {} as Dictionary);
    }
    setDict(_dict);
    // cache the dictionary
    dictionaryCache[cacheKeyRef.current] = _dict;
  }, [drows, cacheKeyRef]);

  const record = useRecording(appUid);
  const t = useCallback(
    (fullText: string | null | undefined): string => {
      if (!fullText) {
        return '';
      }
      const dictCode =
        fullText.length > 128 ? fullText.substring(0, 128) : fullText;

      if (enableRecording) {
        record(fullText);
      }

      if (!lang || lang.primaryLanguage === 'Y') {
        return fullText;
      }
      return dict[dictCode] ?? fullText;
    },
    [enableRecording, lang, dict, record],
  );

  const value = useMemo(() => ({ t }), [t]);

  globalT = t;

  return (
    <TranslateContext.Provider value={value}>
      {children}
    </TranslateContext.Provider>
  );
}
